// src/components/superuser/Billing.js
//
// Billing overview for the superuser's client — currently in a "pending" state
// (billing isn't enabled yet). It previews the structure so the section feels
// real, and shows the two live usage numbers that will drive a future plan
// (active properties + users). Plan/payment/invoices are placeholders until
// billing is switched on for the client.

import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { firestore } from "../../firebase";
import { clientPropertiesCollection } from "../../utils/firestorePaths";
import PropertyContext from "../../contexts/PropertyContext";

function MetricCard({ label, value, sub }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 600 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.disabled">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

export default function Billing(props) {
  const ctx = useContext(PropertyContext);
  const clientId = ctx.selectedClient || props.selectedClient || props.clientId;

  const [propertyCount, setPropertyCount] = useState(null);
  const [userCount, setUserCount] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    const unsub = onSnapshot(
      query(clientPropertiesCollection(clientId)),
      (snap) => setPropertyCount(snap.size),
      (err) => console.error("billing properties count", err)
    );
    return unsub;
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    const unsub = onSnapshot(
      query(collection(firestore, "users"), where("clientId", "==", clientId)),
      (snap) => setUserCount(snap.size),
      (err) => console.error("billing users count", err)
    );
    return unsub;
  }, [clientId]);

  const dash = (v) => (v == null ? "—" : v);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <ReceiptLongIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Billing
        </Typography>
        <Chip label="Pending" color="warning" size="small" sx={{ fontWeight: 600 }} />
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Billing isn't enabled for this account yet. This is a preview of what
        you'll see once it's activated — your live usage is shown below.
      </Alert>

      {/* Usage / plan snapshot */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <MetricCard label="Current Plan" value="—" sub="Not yet assigned" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard
            label="Active Properties"
            value={dash(propertyCount)}
            sub="Billable units"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard
            label="Users on Account"
            value={dash(userCount)}
            sub="Across all roles"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard label="Est. Monthly" value="—" sub="Pending plan" />
        </Grid>
      </Grid>

      {/* Payment method */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CreditCardIcon fontSize="small" color="disabled" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Payment Method
          </Typography>
        </Box>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No payment method on file.
        </Typography>
        <Button variant="outlined" disabled>
          Add Payment Method
        </Button>
      </Paper>

      {/* Invoices */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Invoices
          </Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No invoices yet.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
